import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import defer from 'lodash/defer';
import {
  Button,
  IconLogo,
  Link,
  TranslationProvider,
  utils,
} from '@resy/ui-kitchen';
import { setLocationPath } from '../../../../../../ui-kitchen/src/utils/locationUtils';
import AmexHeaderLogin from './AmexHeaderLogin';
import selectIsResyRewardsEnabled from '../../ResyRewards/selectors';
import { handleMktAnalytics } from '../AmexUtils';
import { CTA_EVENT_NAME } from '../constants';
import './AmexHeader.scss';

const AmexHeader = ({
  $location,
  $rootScope,
  $routeParams,
  Analytics,
  isAuthenticated,
  isGdaEnabled,
  isNonUSLocation,
  isResyRewardsEnabled,
  matchMedia,
  resyHeaderConfig,
  dateLimitSelector,
  timeLimitSelector,
  showLinkBar,
}) => {
  const amexOffersURL = 'https://www.americanexpress.com/en-us/credit-cards/offers/resy/?eep=46100&extlink=resy-homepage-nav-panel';
  const resolution = matchMedia.lg ? 'desktop' : 'mobile';
  const context = utils.getPageContext(window?.location?.pathname) ?? 'homepage';

  const { isScrolled, previewMode } = $rootScope;

  const onLogoClickAmexmarketing = () => {
    const extraPrams = {
      action_name: 'Header Resy Logo - Click',
    };
    handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraPrams);
    $location.path('/');
    $rootScope.$digest();
  };
  const exclusiveOffersLabel = 'exclusive offers';
  const restaurantsLabel = 'restaurants';
  const resyCreditLabel = 'resy credit';
  const gdaLabel = 'global dining access';
  const resyRewardsLabel = 'resy rewards';

  const exclusiveOffersText = 'U.S. Card Offers from Amex';
  const restaurantsText = 'For Restaurants';
  const resyCreditText = 'The Resy Credit';
  const gdaText = 'Global Dining Access';
  const resyRewardsText = 'Resy Rewards';

  const handleLinkClick = (label, hrefValue) => {
    const extraPrams = {
      action_name: 'Header Top Nav Link - Click',
      item_name: label,
      context,
    };
    handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraPrams);
    if (hrefValue) setLocationPath(hrefValue, () => defer(() => $rootScope.$apply()));
  };

  const renderLinkBar = () => (
    <nav className="AmexHeader__linkbar" aria-label="Resy top bar">
      <div className="TopNav">
        {$rootScope?.previewMode && <div className="TopNav--disabled" />}
        <div className="container TopNav__container">
          {!isNonUSLocation && (
            <>
              <TranslationProvider translationKey="footer.amex_offers">
                <Link
                  href={amexOffersURL}
                  onContextMenu={() => handleLinkClick(exclusiveOffersLabel)}
                  onClick={() => {
                    handleLinkClick(exclusiveOffersLabel);
                  }}
                >
                  {exclusiveOffersText}
                </Link>
              </TranslationProvider>
            </>
          )}
          {isGdaEnabled && (
            <Link
              href="/global-dining-access"
              onContextMenu={() => handleLinkClick(gdaLabel)}
              onClick={() => {
                handleLinkClick(gdaLabel);
              }}
            >
              {gdaText}
            </Link>
          )}
          {!isNonUSLocation && (
            <>
              <TranslationProvider translationKey="resy_credit">
                <Link
                  href="/resycredit?intlink=us-resy-topnav"
                  onContextMenu={() => handleLinkClick(resyCreditLabel)}
                  onClick={() => {
                    handleLinkClick(resyCreditLabel);
                  }}
                >
                  {resyCreditText}
                </Link>
              </TranslationProvider>
            </>
          )}
          {isResyRewardsEnabled && (
            <TranslationProvider translationKey="resy_rewards">
              <Link
                href="/resy-rewards"
                onContextMenu={() => handleLinkClick(resyRewardsLabel)}
                onClick={() => {
                  handleLinkClick(resyRewardsLabel);
                }}
              >
                {resyRewardsText}
              </Link>
            </TranslationProvider>
          )}
          <TranslationProvider translationKey="for_restaurants">
            <Link
              href="https://resy.com/join/"
              onContextMenu={() => handleLinkClick(restaurantsLabel)}
              onClick={() => {
                handleLinkClick(restaurantsLabel);
              }}
              target="_self"
            >
              {restaurantsText}
            </Link>
          </TranslationProvider>
        </div>
      </div>
    </nav>
  );

  const renderHeader = () => (
    <header
      aria-label="Main Resy"
      className={`AmexHeader AmexHeader--${resolution} ${isScrolled ? 'AmexNav--scrolled' : ''}`}
    >
      <div className={`${previewMode ? 'AmexHeader--disabled' : ''}`} />
      <div className="AmexNav__primary container">
        <div
          className={`AmexNav__${matchMedia.lg ? 'container' : 'close-container'}`}
        >
          <Button
            variant="no-style"
            type="button"
            className="AmexNav__logo"
            aria-label="Resy Logo. Click to go home."
            onClick={() => {
              onLogoClickAmexmarketing();
            }}
          >
            <IconLogo width="77px" height="34px" />
          </Button>

          <div className="AmexNav__close-container__spacer" />
          <AmexHeaderLogin
            $rootScope={$rootScope}
            Analytics={Analytics}
            $location={$location}
            $routeParams={$routeParams}
            isAuthenticated={isAuthenticated}
            matchMedia={matchMedia}
            resyHeaderConfig={resyHeaderConfig}
            dateLimitSelector={dateLimitSelector}
            timeLimitSelector={timeLimitSelector}
          />
        </div>
      </div>
    </header>
  );

  return (
    <>
      {showLinkBar && matchMedia.lg && renderLinkBar()}
      <div className={`AmexNav ${isScrolled ? 'AmexNav--scrolled' : ''}`}>
        {renderHeader()}
      </div>
    </>
  );
};

AmexHeader.propTypes = {
  $location: PropTypes.shape({
    path: PropTypes.func,
  }).isRequired,
  $rootScope: PropTypes.shape({
    $digest: PropTypes.func,
    nav: PropTypes.shape(),
    isScrolled: PropTypes.bool.isRequired,
    previewMode: PropTypes.bool.isRequired,
    $apply: PropTypes.func,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isNonUSLocation: PropTypes.bool.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  $routeParams: PropTypes.shape({}).isRequired,
  isGdaEnabled: PropTypes.bool,
  isResyRewardsEnabled: PropTypes.bool,
  resyHeaderConfig: PropTypes.shape({}),
  dateLimitSelector: PropTypes.shape({}),
  timeLimitSelector: PropTypes.shape({}),
  showLinkBar: PropTypes.bool,
};

AmexHeader.defaultProps = {
  isGdaEnabled: false,
  isResyRewardsEnabled: false,
  resyHeaderConfig: {},
  dateLimitSelector: null,
  timeLimitSelector: null,
  showLinkBar: true,
};

const mapStateToProps = (state) => ({
  isGdaEnabled: state.resyConfig.config?.enable_global_dining_access,
  isResyRewardsEnabled: selectIsResyRewardsEnabled(state),
});

export { AmexHeader as ComponentUnderTest, mapStateToProps };
export default connect(mapStateToProps)(AmexHeader);
